/**
 * Punkte sind Brüche, kein Gleitkomma.
 *
 * Relay führt sie als `maxPointsNumerator`/`maxPointsDenominator` am `ExamItem` und am
 * `ExamAnswer`, das Vergebene als `pointsNumerator`/`pointsDenominator` im `ExamLog`. Damit
 * ist ein halber Punkt `1/2` statt `0.5`, und eine Summe aus Dritteln bleibt genau. In eine
 * Zahl umgerechnet wird erst beim Anzeigen.
 */

export interface Fraction {
  n: number;
  d: number;
}

const ggt = (a: number, b: number): number => (b === 0 ? Math.abs(a) : ggt(b, a % b));

/**
 * Zähler und Nenner bleiben `number`, wie im Brief festgelegt — kein Umbau auf `BigInt`, das
 * änderte die Schnittstelle und zöge alle Aufrufstellen erneut nach. `addFrac` kürzt bei jedem
 * Schritt per GGT, begrenzt den Nenner dabei aber nicht: eine Summe aus Termen mit paarweise
 * teilerfremden Nennern lässt ihn multiplikativ wachsen. Träfe er `Number.MAX_SAFE_INTEGER`,
 * rechnete JavaScript still und falsch weiter, ohne Fehler und ohne Warnung — die schlimmste
 * Fehlerart in einer Punkte-App. Deshalb die laute Schranke hier, am einzigen Ort, an dem jeder
 * Bruch entsteht: `addFrac`, `fromNumber` und `scaleStep` rufen alle `frac` auf, bevor
 * sie ihr Ergebnis herausgeben.
 */
function wehrtÜberlaufAb(n: number, d: number): void {
  if (Math.abs(n) > Number.MAX_SAFE_INTEGER || Math.abs(d) > Number.MAX_SAFE_INTEGER) {
    throw new Error(
      `Bruch ${n}/${d} überschreitet Number.MAX_SAFE_INTEGER — die Rechnung würde still ` +
        'ungenau. Abbruch statt leiser Falschrechnung.',
    );
  }
}

export function frac(n: number, d = 1): Fraction {
  if (d === 0) throw new Error('Nenner darf nicht null sein.');
  wehrtÜberlaufAb(n, d);
  const vorzeichen = d < 0 ? -1 : 1;
  const zähler = n * vorzeichen;
  const nenner = d * vorzeichen;
  if (zähler === 0) return { n: 0, d: 1 };
  const teiler = ggt(zähler, nenner);
  return { n: zähler / teiler, d: nenner / teiler };
}

export function addFrac(a: Fraction, b: Fraction): Fraction {
  return frac(a.n * b.d + b.n * a.d, a.d * b.d);
}

export function toNumber(f: Fraction): number {
  return f.n / f.d;
}

export function eqFrac(a: Fraction, b: Fraction): boolean {
  return a.n * b.d === b.n * a.d;
}

/**
 * Eine Zahl in einen Bruch überführen. Für die Übergangszeit, solange Beispieldaten und
 * Oberfläche noch Zahlen führen.
 *
 * Gesucht wird der **einfachste** Bruch, der die Zahl trifft — über Kettenbrüche, Nenner bis
 * 1000. Eine feste Stufung (etwa Zwanzigstel) genügt nicht: eine zehnstufige Skala mit
 * Höchstwert 5 hat Neuntel-Abstände, und gerundet liefen Anzeige und Ablage auseinander.
 */
export function fromNumber(value: number): Fraction {
  const vorzeichen = value < 0 ? -1 : 1;
  let x = Math.abs(value);
  let [zähler, zählerVor] = [1, 0];
  let [nenner, nennerVor] = [0, 1];

  for (let i = 0; i < 32; i += 1) {
    const ganz = Math.floor(x);
    [zähler, zählerVor] = [ganz * zähler + zählerVor, zähler];
    [nenner, nennerVor] = [ganz * nenner + nennerVor, nenner];
    if (nenner > 1000) {
      [zähler, nenner] = [zählerVor, nennerVor];
      break;
    }
    if (Math.abs(zähler / nenner - Math.abs(value)) < 1e-9) break;
    const rest = x - ganz;
    if (rest < 1e-12) break;
    x = 1 / rest;
  }

  return frac(vorzeichen * zähler, nenner || 1);
}

/**
 * Punkte der Stufe `index` einer linearen Skala mit `steps` Stufen.
 *
 * Skalen sind laut Festlegung des Nutzers **immer linear**: die erste Stufe gibt den Höchstwert,
 * die letzte 0, dazwischen wird gleichmäßig geteilt. Deshalb ist die gewählte Stufe aus der
 * Punktzahl eindeutig zurückzurechnen und muss nicht zusätzlich gespeichert werden.
 *
 * ⚠️ **Beste Stufe zuerst** (Nutzer, 2026-11-02): so stehen die Stufen in allen Relay- und
 * Classic-Paketen („sehr gut … durchgefallen“, „ja, nein“). Bis dahin galt „erste Stufe = 0“,
 * und „sehr gut“ ging mit 0 Punkten hinaus.
 */
export function scaleStep(max: Fraction, index: number, steps: number): Fraction {
  if (steps < 2) throw new Error('Eine Skala braucht mindestens zwei Stufen.');
  return frac(max.n * (steps - 1 - index), max.d * (steps - 1));
}
